<div class="modal-header">
  <h4 class="modal-title">{{ isReservationMode() ? 'Reservation Details' : 'Participant Details' }}</h4>
  <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body">

  <div *ngIf="loading">
    <h3 class="text-muted">Loading...</h3>
  </div>

  <div class="alert alert-danger" role="alert" *ngIf="serverError">
    Something went wrong loading these details (error {{ serverError }}).
  </div>

  <ng-container *ngIf="!loading && details">

    <!-- Event info. The reservation mode has no event input: its payload
         carries the event, and covers every session rather than one part. -->
    <p class="mb-3" *ngIf="isReservationMode()">
      <a *ngIf="getEventDetailsLink() as eventLink; else eventNameOnly"
         [routerLink]="eventLink"><b>{{ getReservationDetails().event_type_name }}</b></a>
      <ng-template #eventNameOnly><b>{{ getReservationDetails().event_type_name }}</b></ng-template>
      <ng-container *ngIf="getReservationDetails().event_start_datetime">
        <br>
        {{ dateStringFromISO8601(getReservationDetails().event_start_datetime, getTimeZone()) }},
        {{ timeStringFromISO8601(getReservationDetails().event_start_datetime, getTimeZone()) }}
      </ng-container>
      <ng-container *ngIf="getReservationDetails().event_num_parts > 1">
        <br>{{ getReservationDetails().event_num_parts }} parts
      </ng-container>
    </p>

    <p class="mb-3" *ngIf="!isReservationMode()">
      <b>{{ event.name }}</b><br>
      {{ dateStringFromISO8601(event.start_datetime, event.time_zone) }},
      {{ timeStringFromISO8601(event.start_datetime, event.time_zone) }}
      <ng-container *ngIf="event.event_part_count > 1">
        <br>Part {{ event.event_part_number }} of {{ event.event_part_count }}
      </ng-container>
    </p>

    <dl class="row mb-0">

      <dt class="col-sm-4">Participant</dt>
      <dd class="col-sm-8">
        <a *ngIf="details.customer_id"
           [routerLink]="['/customers', details.customer_id]"
           target="_blank">
          {{ details.first_name }} {{ details.last_name }}
        </a>
        <span *ngIf="!details.customer_id">
          {{ details.first_name }} {{ details.last_name }}
        </span>
        <ng-container *ngIf="details.preferred_name && (details.preferred_name !== details.first_name)">
          ({{ details.preferred_name }})
        </ng-container>
      </dd>

      <dt class="col-sm-4">Age / Birthday</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="details.birthday">
          {{ getAge(details.birthday) }} years old
          ({{ dateStringFromISO8601(details.birthday) }})
        </ng-container>
        <span class="text-muted" *ngIf="!details.birthday">Unknown</span>
      </dd>

      <ng-container *ngIf="isReservationMode()">
        <dt class="col-sm-4">Sessions</dt>
        <dd class="col-sm-8">
          <div *ngFor="let part of getParts()">
            Part {{ part.event_part_number }}:
            {{ dateStringFromISO8601(part.start_datetime, getTimeZone()) }},
            {{ timeStringFromISO8601(part.start_datetime, getTimeZone()) }}
            &mdash;
            <span *ngIf="part.has_checked_in">Checked in</span>
            <span class="text-muted" *ngIf="!part.has_checked_in">Not checked in</span>
          </div>
        </dd>
      </ng-container>

      <ng-container *ngIf="!isReservationMode()">
        <dt class="col-sm-4">Check-In Status</dt>
        <dd class="col-sm-8">
          <span *ngIf="details.has_checked_in">Checked in</span>
          <span class="text-muted" *ngIf="!details.has_checked_in">Not checked in</span>
        </dd>
      </ng-container>

      <dt class="col-sm-4">Reservation Status</dt>
      <dd class="col-sm-8">
        <span *ngIf="isReservationCancelled()" class="text-danger"><b>Cancelled</b></span>
        <span *ngIf="!isReservationCancelled()">{{ getReservationStatusDisplay() }}</span>
      </dd>

      <ng-container *ngIf="isReservationCancelled()">
        <dt class="col-sm-4">Cancellation Reason</dt>
        <dd class="col-sm-8">{{ getCancellationReasonDisplay() }}</dd>
      </ng-container>

      <ng-container *ngIf="getIncludesEntryDisplay() as includesEntryDisplay">
        <dt class="col-sm-4">Includes facility entry?</dt>
        <dd class="col-sm-8">
          <span *ngIf="includesEntryDisplay === 'Yes'">Yes</span>
          <span class="text-muted" *ngIf="includesEntryDisplay === 'No'">No</span>
        </dd>
      </ng-container>

      <dt class="col-sm-4">Booking Customer</dt>
      <dd class="col-sm-8">
        <a *ngIf="details.booked_by_id"
           [routerLink]="['/customers', details.booked_by_id]"
           target="_blank">
          {{ details.booked_by_first_name }} {{ details.booked_by_last_name }}
        </a>
        <span class="text-muted" *ngIf="!details.booked_by_id">None</span>
      </dd>

      <dt class="col-sm-4">Other Participants</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="isReservationMode(); else eventModeOtherParticipants">
          <div *ngFor="let participant of getOtherReservations()">
            <a [routerLink]="'/reporting/reservations'"
               [queryParams]="getOtherReservationQueryParams(participant)"
               target="_blank">
              {{ participant.first_name }} {{ participant.last_name }}
            </a>
            <span class="text-muted" *ngIf="participant.is_cancelled">(Cancelled)</span>
          </div>
          <span class="text-muted" *ngIf="getOtherReservations().length === 0">None</span>
        </ng-container>
        <ng-template #eventModeOtherParticipants>
          <div *ngFor="let participant of getOtherParticipants()">
            <a [routerLink]="currentPath"
               [queryParams]="getOtherParticipantQueryParams(participant)"
               target="_blank">
              {{ participant.first_name }} {{ participant.last_name }}
            </a>
            <span class="text-muted" *ngIf="participant.is_cancelled">(Cancelled)</span>
          </div>
          <span class="text-muted" *ngIf="getOtherParticipants().length === 0">None</span>
        </ng-template>
      </dd>

      <dt class="col-sm-4">Participant Questions</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="getQuestionNames(details.question_responses).length > 0; else noParticipantQuestions">
          <div *ngFor="let questionName of getQuestionNames(details.question_responses)" class="mb-2">
            {{ questionName }}:
            <div *ngIf="propExistsAndIsNonEmpty(details.question_responses[questionName])"
                 class="question-answer">{{ formatQuestionAnswer(details.question_responses[questionName]) }}</div>
            <div class="text-muted" *ngIf="!propExistsAndIsNonEmpty(details.question_responses[questionName])">
              No response
            </div>
          </div>
        </ng-container>
        <ng-template #noParticipantQuestions>
          <span class="text-muted">None</span>
        </ng-template>
      </dd>

      <dt class="col-sm-4">Booking Questions</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="getQuestionNames(details.booking_question_responses).length > 0; else noBookingQuestions">
          <div *ngFor="let questionName of getQuestionNames(details.booking_question_responses)" class="mb-2">
            {{ questionName }}:
            <div *ngIf="propExistsAndIsNonEmpty(details.booking_question_responses[questionName])"
                 class="question-answer">{{ formatQuestionAnswer(details.booking_question_responses[questionName]) }}</div>
            <div class="text-muted" *ngIf="!propExistsAndIsNonEmpty(details.booking_question_responses[questionName])">
              No response
            </div>
          </div>
        </ng-container>
        <ng-template #noBookingQuestions>
          <span class="text-muted">None</span>
        </ng-template>
      </dd>

      <dt class="col-sm-4">Created At</dt>
      <dd class="col-sm-8">
        {{ dateStringFromISO8601(details.created_at, getTimeZone()) }},
        {{ timeStringFromISO8601(details.created_at, getTimeZone()) }}
      </dd>

      <dt class="col-sm-4">Last Updated At</dt>
      <dd class="col-sm-8">
        {{ dateStringFromISO8601(details.updated_at, getTimeZone()) }},
        {{ timeStringFromISO8601(details.updated_at, getTimeZone()) }}
      </dd>

      <dt class="col-sm-4">Booking ID</dt>
      <dd class="col-sm-8">
        <a [routerLink]="'/reporting/bookings'"
           [queryParams]="getBookingQueryParams()"
           target="_blank">
          {{ details.booking_id }}
        </a>
      </dd>

      <dt class="col-sm-4">Reservation ID</dt>
      <dd class="col-sm-8">{{ details.reservation_id }}</dd>

    </dl>

  </ng-container>

</div>

<div class="modal-footer justify-content-between" ngPreserveWhitespaces
     *ngIf="!isReservationMode()">
  <div>
    <button *ngIf="!reservation.has_checked_in"
            type="button"
            class="btn btn-outline-primary"
            (click)="handleToggleCheckIn()">
      Check In
    </button>
    <button *ngIf="reservation.has_checked_in"
            type="button"
            disabled
            class="btn btn-success disabled">
      Checked In
    </button>
  </div>
  <div>
    <a *ngIf="reservation.booking_payment_url && !reservation.linked_private_event_id"
       [href]="reservation.booking_payment_url"
       target="_blank"
       rel="noopener"
       class="btn btn-outline-primary"
       ngbTooltip="Click to take payment now (card reader included). Right-click to copy a link the payer can use without logging in."
       container="body"
       (click)="handleCompletePayment($event)">
      Complete Payment
    </a>
    <button *ngIf="!reservation.is_cancelled && !reservation.has_checked_in && !event.is_private && !event.is_private_event_request_form && (event.roster_id === null)"
            type="button"
            class="btn btn-outline-primary"
            (click)="handleRebook()">
      Rebook
    </button>
    <button *ngIf="!reservation.is_cancelled && !reservation.has_checked_in && !event.is_private"
            type="button"
            class="btn btn-outline-danger"
            (click)="handleCancel()">
      Cancel
    </button>
    <button *ngIf="reservation.has_checked_in"
            type="button"
            class="btn btn-outline-danger"
            (click)="handleToggleCheckIn()">
      Undo Check-In
    </button>
  </div>
</div>
